export const THEME_SHADES = [
  50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950,
] as const;

export type ThemeShade = (typeof THEME_SHADES)[number];

export type FormThemeShades = Record<ThemeShade, string>;

export interface FormThemePreset {
  id: string;
  label: string;
  shades: FormThemeShades;
  /** Shade used for submit/accent + swatch (preserves current look). */
  accentShade: ThemeShade;
}

/** Official Tailwind CSS default palettes (50–950). */
export const FORM_THEME_PRESETS: FormThemePreset[] = [
  {
    id: 'red',
    label: 'Red',
    accentShade: 600,
    shades: {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#ef4444',
      600: '#dc2626',
      700: '#b91c1c',
      800: '#991b1b',
      900: '#7f1d1d',
      950: '#450a0a',
    },
  },
  {
    id: 'orange',
    label: 'Orange',
    accentShade: 600,
    shades: {
      50: '#fff7ed',
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#fb923c',
      500: '#f97316',
      600: '#ea580c',
      700: '#c2410c',
      800: '#9a3412',
      900: '#7c2d12',
      950: '#431407',
    },
  },
  {
    id: 'amber',
    label: 'Amber',
    accentShade: 600,
    shades: {
      50: '#fffbeb',
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
      800: '#92400e',
      900: '#78350f',
      950: '#451a03',
    },
  },
  {
    id: 'yellow',
    label: 'Yellow',
    accentShade: 600,
    shades: {
      50: '#fefce8',
      100: '#fef9c3',
      200: '#fef08a',
      300: '#fde047',
      400: '#facc15',
      500: '#eab308',
      600: '#ca8a04',
      700: '#a16207',
      800: '#854d0e',
      900: '#713f12',
      950: '#422006',
    },
  },
  {
    id: 'green',
    label: 'Green',
    accentShade: 600,
    shades: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
      950: '#052e16',
    },
  },
  {
    id: 'emerald',
    label: 'Emerald',
    accentShade: 600,
    shades: {
      50: '#ecfdf5',
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981',
      600: '#059669',
      700: '#047857',
      800: '#065f46',
      900: '#064e3b',
      950: '#022c22',
    },
  },
  {
    id: 'sky',
    label: 'Sky',
    accentShade: 600,
    shades: {
      50: '#f0f9ff',
      100: '#e0f2fe',
      200: '#bae6fd',
      300: '#7dd3fc',
      400: '#38bdf8',
      500: '#0ea5e9',
      600: '#0284c7',
      700: '#0369a1',
      800: '#075985',
      900: '#0c4a6e',
      950: '#082f49',
    },
  },
  {
    id: 'blue',
    label: 'Blue',
    accentShade: 600,
    shades: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
      950: '#172554',
    },
  },
  {
    id: 'indigo',
    label: 'Indigo',
    accentShade: 600,
    shades: {
      50: '#eef2ff',
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#6366f1',
      600: '#4f46e5',
      700: '#4338ca',
      800: '#3730a3',
      900: '#312e81',
      950: '#1e1b4b',
    },
  },
  {
    id: 'violet',
    label: 'Violet',
    accentShade: 600,
    shades: {
      50: '#f5f3ff',
      100: '#ede9fe',
      200: '#ddd6fe',
      300: '#c4b5fd',
      400: '#a78bfa',
      500: '#8b5cf6',
      600: '#7c3aed',
      700: '#6d28d9',
      800: '#5b21b6',
      900: '#4c1d95',
      950: '#2e1065',
    },
  },
  {
    id: 'purple',
    label: 'Purple',
    accentShade: 600,
    shades: {
      50: '#faf5ff',
      100: '#f3e8ff',
      200: '#e9d5ff',
      300: '#d8b4fe',
      400: '#c084fc',
      500: '#a855f7',
      600: '#9333ea',
      700: '#7e22ce',
      800: '#6b21a8',
      900: '#581c87',
      950: '#3b0764',
    },
  },
  {
    id: 'pink',
    label: 'Pink',
    accentShade: 600,
    shades: {
      50: '#fdf2f8',
      100: '#fce7f3',
      200: '#fbcfe8',
      300: '#f9a8d4',
      400: '#f472b6',
      500: '#ec4899',
      600: '#db2777',
      700: '#be185d',
      800: '#9d174d',
      900: '#831843',
      950: '#500724',
    },
  },
  {
    id: 'rose',
    label: 'Rose',
    accentShade: 600,
    shades: {
      50: '#fff1f2',
      100: '#ffe4e6',
      200: '#fecdd3',
      300: '#fda4af',
      400: '#fb7185',
      500: '#f43f5e',
      600: '#e11d48',
      700: '#be123c',
      800: '#9f1239',
      900: '#881337',
      950: '#4c0519',
    },
  },
  {
    id: 'slate',
    label: 'Slate',
    accentShade: 900,
    shades: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
      950: '#020617',
    },
  },
];

const SLATE_PRESET = FORM_THEME_PRESETS[FORM_THEME_PRESETS.length - 1];

export const DEFAULT_FORM_THEME_SHADES: FormThemeShades = { ...SLATE_PRESET.shades };
export const DEFAULT_FORM_THEME_ACCENT_SHADE: ThemeShade = SLATE_PRESET.accentShade;

/** @deprecated Use DEFAULT_FORM_THEME_SHADES / themeAccentHex */
export const DEFAULT_FORM_THEME_COLOR = SLATE_PRESET.shades[SLATE_PRESET.accentShade];

const THEME_COLOR_HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;

/** White/black mix amounts to build a scale around shade 600. */
const SHADE_MIX: Record<ThemeShade, { toward: 'white' | 'black' | 'none'; amount: number }> = {
  50: { toward: 'white', amount: 0.95 },
  100: { toward: 'white', amount: 0.9 },
  200: { toward: 'white', amount: 0.75 },
  300: { toward: 'white', amount: 0.6 },
  400: { toward: 'white', amount: 0.4 },
  500: { toward: 'white', amount: 0.2 },
  600: { toward: 'none', amount: 0 },
  700: { toward: 'black', amount: 0.15 },
  800: { toward: 'black', amount: 0.3 },
  900: { toward: 'black', amount: 0.45 },
  950: { toward: 'black', amount: 0.6 },
};

function cloneShades(shades: FormThemeShades): FormThemeShades {
  return { ...shades };
}

function isFormThemeShades(value: unknown): value is FormThemeShades {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return THEME_SHADES.every(
    (shade) =>
      typeof record[shade] === 'string' &&
      THEME_COLOR_HEX_PATTERN.test(record[shade] as string),
  );
}

function shadesEqual(a: FormThemeShades, b: FormThemeShades): boolean {
  return THEME_SHADES.every(
    (shade) => a[shade].toLowerCase() === b[shade].toLowerCase(),
  );
}

function presetByAccentHex(hex: string): FormThemePreset | undefined {
  const normalized = hex.trim().toLowerCase();
  return FORM_THEME_PRESETS.find(
    (preset) => preset.shades[preset.accentShade].toLowerCase() === normalized,
  );
}

function findPresetByShades(shades: FormThemeShades): FormThemePreset | undefined {
  return FORM_THEME_PRESETS.find((preset) => shadesEqual(preset.shades, shades));
}

function customPresetFromShades(shades: FormThemeShades): FormThemePreset {
  return {
    id: 'custom',
    label: 'Custom',
    shades: cloneShades(shades),
    accentShade: 600,
  };
}

function normalizeHexInput(value: string): string | null {
  let hex = value.trim();
  if (!hex) {
    return null;
  }
  if (!hex.startsWith('#')) {
    hex = `#${hex}`;
  }
  if (!THEME_COLOR_HEX_PATTERN.test(hex)) {
    return null;
  }
  return hex.toLowerCase();
}

function parseHexRgb(hex: string): { r: number; g: number; b: number } | null {
  const normalized = normalizeHexInput(hex);
  if (!normalized) {
    return null;
  }
  return {
    r: Number.parseInt(normalized.slice(1, 3), 16),
    g: Number.parseInt(normalized.slice(3, 5), 16),
    b: Number.parseInt(normalized.slice(5, 7), 16),
  };
}

function toHex({ r, g, b }: { r: number; g: number; b: number }): string {
  const channel = (n: number) =>
    Math.round(Math.min(255, Math.max(0, n)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

function mixRgb(
  base: { r: number; g: number; b: number },
  toward: { r: number; g: number; b: number },
  amount: number,
): { r: number; g: number; b: number } {
  return {
    r: base.r + (toward.r - base.r) * amount,
    g: base.g + (toward.g - base.g) * amount,
    b: base.b + (toward.b - base.b) * amount,
  };
}

/**
 * Build a full 50–950 palette from a single hex (used as shade 600).
 * Returns null when the hex is invalid.
 */
export function generateThemeShadesFromHex(hex: string): FormThemeShades | null {
  const base = parseHexRgb(hex);
  if (!base) {
    return null;
  }

  const white = { r: 255, g: 255, b: 255 };
  const black = { r: 0, g: 0, b: 0 };
  const shades = {} as FormThemeShades;

  for (const shade of THEME_SHADES) {
    const mix = SHADE_MIX[shade];
    if (mix.toward === 'none') {
      shades[shade] = toHex(base);
      continue;
    }
    const target = mix.toward === 'white' ? white : black;
    shades[shade] = toHex(mixRgb(base, target, mix.amount));
  }

  return shades;
}

export function normalizeThemeHex(value: string): string | null {
  return normalizeHexInput(value);
}

export function isCustomThemeShades(
  value: FormThemeShades | string | null | undefined,
): boolean {
  if (typeof value === 'string' || !isFormThemeShades(value)) {
    return false;
  }
  return !findPresetByShades(value);
}

/** Resolve stored theme (shade map or legacy hex string) to a known palette. */
export function resolveThemeShades(
  value: FormThemeShades | string | null | undefined,
): FormThemeShades {
  if (typeof value === 'string') {
    const match = presetByAccentHex(value);
    if (match) {
      return cloneShades(match.shades);
    }
    const generated = generateThemeShadesFromHex(value);
    return generated ?? cloneShades(DEFAULT_FORM_THEME_SHADES);
  }

  if (!isFormThemeShades(value)) {
    return cloneShades(DEFAULT_FORM_THEME_SHADES);
  }

  const match = findPresetByShades(value);
  return cloneShades(match?.shades ?? value);
}

export function themePresetForShades(
  value: FormThemeShades | string | null | undefined,
): FormThemePreset {
  const shades = resolveThemeShades(value);
  return findPresetByShades(shades) ?? customPresetFromShades(shades);
}

export function themeAccentHex(
  shades: FormThemeShades | string | null | undefined,
  accentShade?: ThemeShade,
): string {
  const resolved = resolveThemeShades(shades);
  const preset = themePresetForShades(resolved);
  const shade = accentShade ?? preset.accentShade;
  return resolved[shade];
}

export function themeCssVars(
  shades: FormThemeShades | string | null | undefined,
): Record<string, string> {
  const resolved = resolveThemeShades(shades);
  const vars: Record<string, string> = {
    '--form-theme-color': themeAccentHex(resolved),
  };
  for (const shade of THEME_SHADES) {
    vars[`--form-theme-${shade}`] = resolved[shade];
  }
  return vars;
}

/** @deprecated Use resolveThemeShades / themeAccentHex */
export function resolveThemeColor(value: string | null | undefined): string {
  return themeAccentHex(value);
}

/** @deprecated Use themePresetForShades */
export function themePresetForColor(
  value: string | null | undefined,
): FormThemePreset {
  return themePresetForShades(value);
}
